import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { deleteTrip, getTrip, updateTrip, type Trip } from '../api/trips';
import { HttpError } from '../api/client';
import { StagesSection } from '../components/StagesSection';
import { PhotoGallery } from '../components/PhotoGallery';
import { PostsSection } from '../components/PostsSection';
import { CostsSection } from '../components/CostsSection';
import { StatsSection } from '../components/StatsSection';

export function TripDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!id) return;
    getTrip(id)
      .then(setTrip)
      .catch((e) => {
        if (e instanceof HttpError && e.status === 404) {
          setLoadError('Viaggio non trovato');
        } else {
          setLoadError(e.message ?? 'Errore caricamento');
        }
      });
  }, [id]);

  async function onDelete() {
    if (!trip) return;
    if (!confirm(`Eliminare definitivamente il viaggio "${trip.title}"?\n\nVerranno cancellati anche tappe, post, foto e costi.`))
      return;
    try {
      await deleteTrip(trip.id);
      navigate('/trips', { replace: true });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore eliminazione');
    }
  }

  const [exporting, setExporting] = useState(false);
  async function onExportPdf() {
    if (!trip) return;
    setExporting(true);
    try {
      const res = await fetch(`/api/v1/trips/${trip.id}/pdf`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'fetch' },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error?.message ?? `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Estrai filename dal Content-Disposition se disponibile
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename\*?=(?:UTF-8'')?["']?([^;"']+)/.exec(cd);
      a.download = m?.[1] ? decodeURIComponent(m[1]) : `${trip.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore export PDF');
    } finally {
      setExporting(false);
    }
  }

  if (loadError) {
    return (
      <div className="page">
        <p className="err">{loadError}</p>
        <Link to="/trips" className="btn btn-ghost">
          ← Torna ai viaggi
        </Link>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="page">
        <p className="muted">Caricamento…</p>
      </div>
    );
  }

  if (editing) {
    return (
      <TripEditForm
        trip={trip}
        onSaved={(t) => {
          setTrip(t);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="cd-title-upper">{trip.title}</h1>
        <div className="action-row">
          <button onClick={() => void onExportPdf()} className="btn btn-primary" disabled={exporting}>
            {exporting ? '📖 Generazione…' : '📖 Esporta PDF'}
          </button>
          <button onClick={() => setEditing(true)} className="btn btn-ghost">
            Modifica
          </button>
          <button onClick={onDelete} className="btn btn-danger">
            Elimina
          </button>
        </div>
      </div>

      <div className="trip-meta">
        {(trip.startDate || trip.endDate) && (
          <p>
            <strong>Date:</strong>{' '}
            {trip.startDate ? formatDate(trip.startDate) : '—'} →{' '}
            {trip.endDate ? formatDate(trip.endDate) : '—'}
          </p>
        )}
        {trip.description && (
          <p className="trip-description">{trip.description}</p>
        )}
        <p className="counts">
          <span>{trip.stagesCount} tappe</span>
          <span>·</span>
          <span>{trip.postsCount} post</span>
          <span>·</span>
          <span>{trip.photosCount} foto</span>
        </p>
      </div>

      <StagesSection tripId={trip.id} />

      <PhotoGallery tripId={trip.id} />

      <PostsSection tripId={trip.id} />

      <CostsSection tripId={trip.id} />

      <StatsSection tripId={trip.id} />

      <Link to="/trips" className="btn btn-ghost back">
        ← Torna ai viaggi
      </Link>
    </div>
  );
}

function TripEditForm({
  trip,
  onSaved,
  onCancel,
}: {
  trip: Trip;
  onSaved: (t: Trip) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(trip.title);
  const [description, setDescription] = useState(trip.description ?? '');
  const [startDate, setStartDate] = useState(trip.startDate ?? '');
  const [endDate, setEndDate] = useState(trip.endDate ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const updated = await updateTrip(trip.id, {
        title: title.trim(),
        description: description.trim() || null,
        startDate: startDate || null,
        endDate: endDate || null,
      });
      onSaved(updated);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.apiError.message);
      } else {
        setError('Errore di connessione');
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Modifica viaggio</h1>
      </div>
      <form className="form-card" onSubmit={onSubmit}>
        <label>
          <span>Titolo *</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={200}
          />
        </label>

        <label>
          <span>Descrizione</span>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={5000}
          />
        </label>

        <div className="row">
          <label>
            <span>Data inizio</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            <span>Data fine</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
        </div>

        {error && <p className="err">{error}</p>}

        <div className="actions">
          <button type="button" onClick={onCancel} className="btn btn-ghost" disabled={submitting}>
            Annulla
          </button>
          <button type="submit" disabled={submitting || !title.trim()}>
            {submitting ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </form>
    </div>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
